
import {ViewIdentifiable} from "../../models/common/ViewSettings";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {ApiReturnType, getUserViewSettingsApiRequest} from "../../models/common/ApiReturnType";
import {AppState} from "../../reducers/RootReducer";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {eventNotificationError, eventNotificationSuccess} from "../common/EventNotificationActions";
import {
    CustomerOffice, CustomerOfficeModule, CustomerOfficeProp,
} from "../../models/customers/CustomerOffice";
import { ofType, StateObservable } from "redux-observable";
import { switchMap, map, catchError } from "rxjs/operators";
import { ajax } from "rxjs/ajax";
import { Observable, of } from 'rxjs';
import {Nullable} from "../../models/common/Nullable";
import {Identifier} from "../../models/common/Identifier";
import {RouteConfig, RouteMethods} from "../../config/RouteConfig";
import {sendUserToPage} from "../common/UserViewSettingsActions";
import {CustomerId} from "../../models/customers/Customer";
import {OfficeId} from "../../models/common/Office";

/**
 * base API path to interact with project roles
 */
function apiPath( customerOfficeId: number, id: Nullable<number> = null ): string {
    return APP_CONFIG.baseWeb + "customers/"+ customerOfficeId +"/offices/" + ( id !== null ? id : '');
}

/** union of all project role actions types */
export type CustomerOfficeActionTypes = FetchCustomerOfficesActionTypes
    | FetchCustomerOfficeByIdActionTypes
    | CreateCustomerOfficeActionTypes
    | UpdateCustomerOfficeActionTypes
    | DeleteCustomerOfficeActionTypes
    | RestoreCustomerOfficeActionTypes

/**
 * FETCH CUSTOMER_OFFICE OFFICES BY USER VIEW SETTINGS
 *
 * user view settings will automatically be retrieved from redux store
 */
// TYPESCRIPT REDUX ACTION TYPES
export interface FetchCustomerOfficesAction extends CustomerId, ViewIdentifiable, ReduxAction< typeof FETCH_CUSTOMER_OFFICES > {}
export interface FetchCustomerOfficesSuccessAction extends CustomerId, ViewIdentifiable, ReduxAction<typeof FETCH_CUSTOMER_OFFICES_SUCCESS > {
    payload: ApiReturnType< CustomerOffice[] >
}
export interface FetchCustomerOfficesErrorAction extends CustomerId, ViewIdentifiable, ErrorAction< typeof FETCH_CUSTOMER_OFFICES_ERROR >{}

export type FetchCustomerOfficesActionTypes = FetchCustomerOfficesAction
    | FetchCustomerOfficesErrorAction
    | FetchCustomerOfficesSuccessAction

// REDUX ACTION TYPE CONSTANTS
export const FETCH_CUSTOMER_OFFICES = "FETCH_CUSTOMER_OFFICES";
export const FETCH_CUSTOMER_OFFICES_SUCCESS = "FETCH_CUSTOMER_OFFICES_SUCCESS";
export const FETCH_CUSTOMER_OFFICES_ERROR = "FETCH_CUSTOMER_OFFICES_ERROR";

// REDUX ACTION ACCESSORS FUNCTIONS
export function fetchCustomerOffices(
    customerId: Identifier<number>,
    viewIdentifier: string
) : FetchCustomerOfficesAction
{
    return { type: FETCH_CUSTOMER_OFFICES, viewIdentifier, customerId };
}
export function fetchCustomerOfficesSuccess(
    customerId: Identifier<number>,
    viewIdentifier: string,
    payload: ApiReturnType< CustomerOffice[] >
) : FetchCustomerOfficesSuccessAction
{
    return { type: FETCH_CUSTOMER_OFFICES_SUCCESS, viewIdentifier, payload, customerId };
}
export function fetchCustomerOfficesError(
    customerId: Identifier<number>,
    viewIdentifier: string,
    error: Error
): FetchCustomerOfficesErrorAction
{
    return { type: FETCH_CUSTOMER_OFFICES_ERROR, viewIdentifier, error, customerId };
}

// REDUX OBSERVABLE EPIC
export function fetchCustomerOfficesEpic(
    action$: Observable< FetchCustomerOfficesAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_CUSTOMER_OFFICES ),
        switchMap( action => {
            const request = getUserViewSettingsApiRequest(
                apiPath( action.customerId.rawValue ),
                action.viewIdentifier,
                store$
            );

            return ajax( APP_CONFIG.getRequestObject(request.path) ).pipe(
                map( response => fetchCustomerOfficesSuccess(
                    action.customerId,
                    action.viewIdentifier,
                    response.response as ApiReturnType< CustomerOffice[] >
                ) ),
                catchError(error => of(
                    fetchCustomerOfficesError(
                        action.customerId,
                        action.viewIdentifier,
                        error
                    ),
                    eventNotificationError(error, store$, action.viewIdentifier, "fetchCustomerOffices")
                ))
            );

        })
    );
}

/**
 * FETCH SINGLE CUSTOMER_OFFICE
 */
export interface FetchCustomerOfficeByIdAction extends CustomerId, OfficeId, ReduxAction< typeof FETCH_CUSTOMER_OFFICE_BY_ID> {

}
export interface FetchCustomerOfficeByIdSuccessAction extends CustomerOfficeProp, ReduxAction< typeof FETCH_CUSTOMER_OFFICE_BY_ID_SUCCESS> {

}
export interface FetchCustomerOfficeByIdErrorAction extends OfficeId, CustomerId, ErrorAction< typeof FETCH_CUSTOMER_OFFICE_BY_ID_ERROR> {

}
//union of action to fetch by id
export type FetchCustomerOfficeByIdActionTypes = FetchCustomerOfficeByIdAction
    | FetchCustomerOfficeByIdSuccessAction
    | FetchCustomerOfficeByIdErrorAction

// REDUX ACTION TYPE CONSTANTS
export const FETCH_CUSTOMER_OFFICE_BY_ID = "FETCH_CUSTOMER_OFFICE_BY_ID";
export const FETCH_CUSTOMER_OFFICE_BY_ID_SUCCESS = "FETCH_CUSTOMER_OFFICE_BY_ID_SUCCESS";
export const FETCH_CUSTOMER_OFFICE_BY_ID_ERROR = "FETCH_CUSTOMER_OFFICE_BY_ID_ERROR";

//Redux action accessors function
export function fetchCustomerOfficeById(
    customerId: Identifier<number>,
    officeId: Identifier<number>
): FetchCustomerOfficeByIdAction {
    return { type: FETCH_CUSTOMER_OFFICE_BY_ID, officeId, customerId };
}
export function fetchCustomerOfficeByIdSuccess( customerOffice: CustomerOffice ): FetchCustomerOfficeByIdSuccessAction {
    return { type: FETCH_CUSTOMER_OFFICE_BY_ID_SUCCESS, customerOffice };
}
export function fetchCustomerOfficeByIdError(
    customerId: Identifier<number>,
    officeId: Identifier<number>,
    error: any,
): FetchCustomerOfficeByIdErrorAction {
    return { type: FETCH_CUSTOMER_OFFICE_BY_ID_ERROR, error, officeId, customerId };
}

//redux observable epic as bridge to rxjs
export function fetchCustomerOfficeByIdEpic(
    action$: Observable< FetchCustomerOfficeByIdAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_CUSTOMER_OFFICE_BY_ID ),
        switchMap( action => {
                return ajax(
                    APP_CONFIG.getRequestObject(
                        apiPath(
                            action.customerId.rawValue,
                            action.officeId.rawValue
                        )
                    )
                ).pipe(
                    map( response => fetchCustomerOfficeByIdSuccess( response.response as CustomerOffice ) ),
                    catchError( error => of(
                        fetchCustomerOfficeByIdError( action.customerId, action.officeId, error ),
                        eventNotificationError( error, store$, CustomerOfficeModule.identifier, "fetchCustomerOfficeById" )
                    ) )
                )
            }
        )
    );
}

/**
 * MUTATING REQUESTS
 */

/**
 * successful mutating requests should be send back to the detail page
 */
function sendUserToPageURL( customerId: number ) {
    if( typeof RouteConfig.customers.routes.detail.link !== 'undefined' )
        return RouteConfig.customers.routes.detail.link( customerId );
    else
        return RouteConfig.customers.routes.main.path;
}

/**
 * CREATE CUSTOMER_OFFICE
 */
export interface CreateCustomerOfficeAction extends CustomerId, CustomerOfficeProp, ReduxAction<typeof CREATE_CUSTOMER_OFFICE> {

}
export interface CreateCustomerOfficeSuccessAction extends CustomerId, OfficeId, ReduxAction<typeof CREATE_CUSTOMER_OFFICE_SUCCESS> {

}
export interface CreateCustomerOfficeErrorAction extends CustomerId, ErrorAction<typeof CREATE_CUSTOMER_OFFICE_ERROR> {

}
// typescript union type of create actions
export type CreateCustomerOfficeActionTypes = CreateCustomerOfficeAction
    | CreateCustomerOfficeSuccessAction
    | CreateCustomerOfficeErrorAction

//redux action constants
export const CREATE_CUSTOMER_OFFICE = "CREATE_CUSTOMER_OFFICE";
export const CREATE_CUSTOMER_OFFICE_SUCCESS = "CREATE_CUSTOMER_OFFICE_SUCCESS";
export const CREATE_CUSTOMER_OFFICE_ERROR = "CREATE_CUSTOMER_OFFICE_ERROR";

//redux action accessors functions
export function createCustomerOffice( customerOffice: CustomerOffice ): CreateCustomerOfficeAction {
    return { type: CREATE_CUSTOMER_OFFICE, customerOffice, customerId: customerOffice.customerId };
}
export function createCustomerOfficeSuccess(
    customerId: Identifier<number>,
    officeId: Identifier<number>
): CreateCustomerOfficeSuccessAction
{
    return { type: CREATE_CUSTOMER_OFFICE_SUCCESS, officeId, customerId };
}
export function createCustomerOfficeError(
    customerId: Identifier< number>,
    error: any
): CreateCustomerOfficeErrorAction
{
    return { type: CREATE_CUSTOMER_OFFICE_ERROR, error, customerId };
}

//redux observable rxjs bridge
export function createCustomerOfficeEpic(
    action$: Observable< CreateCustomerOfficeAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( CREATE_CUSTOMER_OFFICE ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath( action.customerOffice.customerId.rawValue ),
                    RouteMethods.post,
                    action.customerOffice
                )
            ).pipe(
                switchMap( _ => of(
                    createCustomerOfficeSuccess(
                        action.customerOffice.customerId,
                        action.customerOffice.officeId
                    ),
                    eventNotificationSuccess(),
                    sendUserToPage(
                        sendUserToPageURL( action.customerOffice.customerId.rawValue ),
                        CustomerOfficeModule.identifier
                    )
                ) ),
                catchError( error => of(
                    createCustomerOfficeError( action.customerOffice.customerId, error ),
                    eventNotificationError( error, store$, CustomerOfficeModule.identifier, "createCustomerOffice" )
                ))
            )
        )
    )
}

/**
 * UPDATE CUSTOMER_OFFICE
 */
export interface UpdateCustomerOfficeAction extends CustomerId, CustomerOfficeProp, ReduxAction<typeof UPDATE_CUSTOMER_OFFICE> {

}
export interface UpdateCustomerOfficeSuccessAction extends CustomerId, OfficeId, ReduxAction<typeof UPDATE_CUSTOMER_OFFICE_SUCCESS> {

}
export interface UpdateCustomerOfficeErrorAction extends CustomerId, ErrorAction<typeof UPDATE_CUSTOMER_OFFICE_ERROR> {

}
// typescript union type of update actions
export type UpdateCustomerOfficeActionTypes = UpdateCustomerOfficeAction
    | UpdateCustomerOfficeSuccessAction
    | UpdateCustomerOfficeErrorAction

//redux action constants
export const UPDATE_CUSTOMER_OFFICE = "UPDATE_CUSTOMER_OFFICE";
export const UPDATE_CUSTOMER_OFFICE_SUCCESS = "UPDATE_CUSTOMER_OFFICE_SUCCESS";
export const UPDATE_CUSTOMER_OFFICE_ERROR = "UPDATE_CUSTOMER_OFFICE_ERROR";

//redux action accessors functions
export function updateCustomerOffice( customerOffice: CustomerOffice ): UpdateCustomerOfficeAction {
    return { type: UPDATE_CUSTOMER_OFFICE, customerOffice, customerId: customerOffice.customerId };
}
export function updateCustomerOfficeSuccess(
    customerId: Identifier<number>,
    officeId: Identifier<number>
): UpdateCustomerOfficeSuccessAction {
    return { type: UPDATE_CUSTOMER_OFFICE_SUCCESS, officeId, customerId };
}
export function updateCustomerOfficeError(
    customerId: Identifier<number>,
    error: any
): UpdateCustomerOfficeErrorAction {
    return { type: UPDATE_CUSTOMER_OFFICE_ERROR, error, customerId };
}

//redux observable rxjs bridge
export function updateCustomerOfficeEpic(
    action$: Observable< UpdateCustomerOfficeAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( UPDATE_CUSTOMER_OFFICE ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath(
                        action.customerOffice.customerId.rawValue,
                        action.customerOffice.officeId.rawValue
                    ),
                    RouteMethods.put,
                    action.customerOffice
                )
            ).pipe(
                switchMap( _ => of(
                    updateCustomerOfficeSuccess(
                        action.customerOffice.customerId,
                        action.customerOffice.officeId
                    ),
                    eventNotificationSuccess(),
                    sendUserToPage(
                        sendUserToPageURL( action.customerOffice.customerId.rawValue ),
                        CustomerOfficeModule.identifier
                    )
                ) ),
                catchError( error => of(
                    updateCustomerOfficeError( action.customerOffice.customerId, error ),
                    eventNotificationError( error, store$, CustomerOfficeModule.identifier, "updateCustomerOffice" )
                ))
            )
        )
    )
}

/**
 * DELETE CUSTOMER_OFFICE
 */
export interface DeleteCustomerOfficeAction extends CustomerId, OfficeId, ReduxAction<typeof DELETE_CUSTOMER_OFFICE> {

}
export interface DeleteCustomerOfficeSuccessAction extends CustomerId, OfficeId, ReduxAction<typeof DELETE_CUSTOMER_OFFICE_SUCCESS> {

}
export interface DeleteCustomerOfficeErrorAction extends CustomerId, ErrorAction<typeof DELETE_CUSTOMER_OFFICE_ERROR> {

}
// typescript union type of delete actions
export type DeleteCustomerOfficeActionTypes = DeleteCustomerOfficeAction
    | DeleteCustomerOfficeSuccessAction
    | DeleteCustomerOfficeErrorAction

//redux action constants
export const DELETE_CUSTOMER_OFFICE = "DELETE_CUSTOMER_OFFICE";
export const DELETE_CUSTOMER_OFFICE_SUCCESS = "DELETE_CUSTOMER_OFFICE_SUCCESS";
export const DELETE_CUSTOMER_OFFICE_ERROR = "DELETE_CUSTOMER_OFFICE_ERROR";

//redux action accessors functions
export function deleteCustomerOffice(
    customerId: Identifier<number>,
    officeId: Identifier<number>
): DeleteCustomerOfficeAction {
    return { type: DELETE_CUSTOMER_OFFICE, officeId, customerId };
}
export function deleteCustomerOfficeSuccess(
    customerId: Identifier<number>,
    officeId: Identifier<number>
): DeleteCustomerOfficeSuccessAction {
    return { type: DELETE_CUSTOMER_OFFICE_SUCCESS, officeId, customerId };
}
export function deleteCustomerOfficeError( customerId: Identifier< number >, error: any ): DeleteCustomerOfficeErrorAction {
    return { type: DELETE_CUSTOMER_OFFICE_ERROR, error, customerId };
}

//redux observable rxjs bridge
export function deleteCustomerOfficeEpic(
    action$: Observable< DeleteCustomerOfficeAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( DELETE_CUSTOMER_OFFICE ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath(
                        action.customerId.rawValue,
                        action.officeId.rawValue
                    ),
                    RouteMethods.delete
                )
            ).pipe(
                switchMap( _ => of(
                    deleteCustomerOfficeSuccess( action.customerId, action.officeId ),
                    eventNotificationSuccess(),
                    fetchCustomerOfficeById( action.customerId, action.officeId )
                ) ),
                catchError( error => of(
                    deleteCustomerOfficeError( action.customerId, error ),
                    eventNotificationError( error, store$, CustomerOfficeModule.identifier, "deleteCustomerOffice" )
                ))
            )
        )
    )
}

/**
 * RESTORE CUSTOMER_OFFICE
 */
export interface RestoreCustomerOfficeAction extends CustomerId, OfficeId, ReduxAction<typeof RESTORE_CUSTOMER_OFFICE> {

}
export interface RestoreCustomerOfficeSuccessAction extends CustomerId, OfficeId, ReduxAction<typeof RESTORE_CUSTOMER_OFFICE_SUCCESS> {

}
export interface RestoreCustomerOfficeErrorAction extends CustomerId, ErrorAction<typeof RESTORE_CUSTOMER_OFFICE_ERROR> {

}
// typescript union type of restore actions
export type RestoreCustomerOfficeActionTypes = RestoreCustomerOfficeAction
    | RestoreCustomerOfficeSuccessAction
    | RestoreCustomerOfficeErrorAction

//redux action constants
export const RESTORE_CUSTOMER_OFFICE = "RESTORE_CUSTOMER_OFFICE";
export const RESTORE_CUSTOMER_OFFICE_SUCCESS = "RESTORE_CUSTOMER_OFFICE_SUCCESS";
export const RESTORE_CUSTOMER_OFFICE_ERROR = "RESTORE_CUSTOMER_OFFICE_ERROR";

//redux action accessors functions
export function restoreCustomerOffice(
    customerId: Identifier<number>,
    officeId: Identifier<number>
): RestoreCustomerOfficeAction {
    return { type: RESTORE_CUSTOMER_OFFICE, officeId, customerId };
}
export function restoreCustomerOfficeSuccess(
    customerId: Identifier<number>,
    officeId: Identifier<number>
): RestoreCustomerOfficeSuccessAction {
    return { type: RESTORE_CUSTOMER_OFFICE_SUCCESS, officeId, customerId };
}
export function restoreCustomerOfficeError(
    customerId: Identifier<number>,
    error: any
): RestoreCustomerOfficeErrorAction {
    return { type: RESTORE_CUSTOMER_OFFICE_ERROR, error, customerId };
}

//redux observable rxjs bridge
export function restoreCustomerOfficeEpic(
    action$: Observable< RestoreCustomerOfficeAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( RESTORE_CUSTOMER_OFFICE ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath(
                        action.customerId.rawValue,
                        action.officeId.rawValue
                    ),
                    RouteMethods.patch
                )
            ).pipe(
                switchMap( _ => of(
                    restoreCustomerOfficeSuccess( action.customerId, action.officeId ),
                    eventNotificationSuccess(),
                    fetchCustomerOfficeById( action.customerId, action.officeId )
                ) ),
                catchError( error => of(
                    restoreCustomerOfficeError( action.customerId, error ),
                    eventNotificationError( error, store$, CustomerOfficeModule.identifier, "restoreCustomerOffice" )
                ))
            )
        )
    )
}